import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import {
  RESERVATIONS_SETTING_KEY,
  holdsByTable,
  reservationsConfigFrom,
  type ReservationHold,
  type ReservationsConfig,
} from '@/lib/reservations'

/**
 * The tenant's reservations switch (F-RSV-N1), read from GET /settings and
 * sharing the ['settings','all'] cache. Off until an admin turns it on.
 */
export function useReservationsConfig(): { config: ReservationsConfig; enabled: boolean; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: Infinity,
    enabled: apiClient.isAuthenticated(),
    retry: false,
  })
  const config = reservationsConfigFrom((data?.data as Record<string, unknown> | undefined)?.[RESERVATIONS_SETTING_KEY])
  return { config, enabled: config.enabled, isLoading }
}

export const RESERVATION_HOLDS_QK = ['reservations', 'holds'] as const

/**
 * Tables the floor shows as "Reserved …" right now, keyed by table id.
 * Polls every 30 s while reservations are on; an empty map otherwise.
 */
export function useReservationHolds(): Map<string, ReservationHold> {
  const { enabled } = useReservationsConfig()
  const { data } = useQuery({
    queryKey: RESERVATION_HOLDS_QK,
    queryFn: () => apiClient.getReservationHolds().then((r) => r.data ?? []),
    enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
  return holdsByTable(enabled ? data : undefined)
}
